/**
 * Shared query-key + URL builders for the feature screens.
 * Keeps query-keys consistent so screens can share cache entries.
 */
import { apiRequest } from '@/lib/api';

export type Paginated<T> = {
  data: T[];
  meta?: {
    page?: number;
    per_page?: number;
    total?: number;
    pages?: number;
  };
};

export type Wrapped<T> = { data: T };

/** Unwrap either a `{ data: ... }` or a raw payload. */
export function unwrap<T>(input: Wrapped<T> | T): T {
  if (input && typeof input === 'object' && 'data' in (input as Record<string, unknown>)) {
    return (input as Wrapped<T>).data;
  }
  return input as T;
}

/** Last N days as ISO date strings (today - n .. today). */
export function lastNDays(n: number): { from: string; to: string } {
  const to = new Date();
  const from = new Date();
  from.setDate(to.getDate() - n);
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

/** Build a query string from a plain record, skipping undefined / null values. */
export function qs(params: Record<string, string | number | undefined | null>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

/** Thin wrapper around apiRequest that returns the inner `data`. */
export async function apiGet<T>(path: string, opts?: { tenant?: string | 'all' }): Promise<T> {
  const res = await apiRequest<Wrapped<T> | T>(path, {
    method: 'GET',
    tenant: opts?.tenant,
  });
  return unwrap<T>(res);
}

/**
 * Variant that preserves the outer envelope so callers can read `meta.total`.
 * Backend pattern: `{ data: T[], meta: { page, per_page, total, pages } }` (Page[T]).
 */
export async function apiGetPaginated<T>(
  path: string,
  opts?: { tenant?: string | 'all' },
): Promise<Paginated<T>> {
  return apiRequest<Paginated<T>>(path, {
    method: 'GET',
    tenant: opts?.tenant,
  });
}

export async function apiPost<T>(
  path: string,
  body: unknown,
  opts?: { tenant?: string | 'all' },
): Promise<T> {
  const res = await apiRequest<Wrapped<T> | T>(path, {
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
    tenant: opts?.tenant,
  });
  return unwrap<T>(res);
}

export async function apiPatch<T>(
  path: string,
  body: unknown,
  opts?: { tenant?: string | 'all' },
): Promise<T> {
  const res = await apiRequest<Wrapped<T> | T>(path, {
    method: 'PATCH',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
    tenant: opts?.tenant,
  });
  return unwrap<T>(res);
}

export async function apiDelete<T = void>(
  path: string,
  opts?: { tenant?: string | 'all' },
): Promise<T> {
  const res = await apiRequest<Wrapped<T> | T>(path, {
    method: 'DELETE',
    tenant: opts?.tenant,
  });
  return unwrap<T>(res);
}
